import {
  IHotelBookingCalendar,
  IHotelBookingCalendarDate,
  IHotelBookingGuest,
  IHotelBookingSummary,
  IHotelPreference,
  IHotelPreferenceCategory,
} from "services/models";
import { toCurrentIntFormat } from "utils";
import { getRandom, getRandomInt } from "./data";

class MockHotelBookingDataStore {
  static mockPrimary = {
    firstName: "Nicholas",
    lastName: "Mata",
    email: "nicholasmata@gmail.com",
    phoneNumber: "123-322-1231",
    clubNumber: 2728174,
    guestId: "ad199907-bbca-4ce0-a14c-7537362b5f53",
    primary: true,
  };

  guests: IHotelBookingGuest[] = [MockHotelBookingDataStore.mockPrimary];
  note: string | null = getRandom(["This guest is important!", null, ""]);
  estimatedTime = "";
  preferences: IHotelPreference[] = [];
  paymentRequired = true
  canBookWithoutPayment = getRandom([true, false]);

  calendar: IHotelBookingCalendar;

  constructor() {
    const hotelBookingDaysOut = 90;
    const hotelBookingCalendarStart = new Date();
    const hotelBookingCalendarEnd = new Date();
    hotelBookingCalendarEnd.setDate(
      hotelBookingCalendarEnd.getDate() + hotelBookingDaysOut
    );
    let hotelBookingCalendarDates: IHotelBookingCalendarDate[] = [];
    for (var i = 0; i < hotelBookingDaysOut; i++) {
      const date = new Date(hotelBookingCalendarStart);
      date.setDate(date.getDate() + i);
      const bookable = getRandom([true, false, true, true]);
      const isComped = getRandom([true, true]);
      const numberRoomsLeft = getRandomInt(1, 10);
      const normalPrice = getRandomInt(800, 1200);

      let actualPrice = getRandomInt(0, normalPrice);
      let actualPriceText = toCurrentIntFormat(actualPrice);
      if (actualPrice < 400) {
        actualPriceText = "FREE";
      }
      hotelBookingCalendarDates.push({
        date: date.toISOString(),
        style: bookable ? "available" : "unavailable",
        bookable: bookable,
        compValue: bookable
          ? isComped
            ? actualPriceText
            : toCurrentIntFormat(normalPrice)
          : null,
        displayValue: bookable
          ? isComped
            ? toCurrentIntFormat(normalPrice)
            : "SOLD OUT"
          : "SOLD OUT",
        importantText: bookable
          ? numberRoomsLeft < 5
            ? `${numberRoomsLeft} LEFT`
            : null
          : null,
      });
    }

    this.calendar = {
      start: hotelBookingCalendarStart.toISOString(),
      end: hotelBookingCalendarEnd.toISOString(),
      dates: hotelBookingCalendarDates,
    };
  }

  get summary(): IHotelBookingSummary {
    return {
      bookingId: "85ca19d5-00fd-452b-8a01-f92c76ee61f7",
      roomType: {
        code: "KR",
        name: "King",
        guestLimit: 2,
        image:
          "https://uxmstagingmediacdn.azureedge.net/hotel/KingRoom/KingRoom_Thrillist-1_Resized640x426.jpg",
      },
      checkInDate: "2022-05-03T23:00:00Z",
      checkOutDate: "2022-05-04T18:00:00Z",
      checkInDisplay: {
        date: "Tue, May 03",
        time: "4:00 PM PST",
      },
      checkOutDisplay: {
        date: "Wed, May 04",
        time: "11:00 AM PST",
      },
      nights: 1,
      secondsToExpire: 589,
      terms:
        "<html><body><p>By booking this reservation I acknowledge and agree to the terms and conditions of my stay.</p><p>Check-in Time: 4pm<br>Checkout Time: 11am</p><p>Standard Deposit Policy: All reservations require One Night Room and Tax. Valid credit card required for incidental deposit, and incremental authorizations will be made to the credit card for charges accrued during the stay.</p><p>Cancellation Policy: Cancellations are accepted until 24 hours before arrival with a full return of the deposit. Any cancellations made after that time period on the date before scheduled arrival will be charged a non-refundable, no show fee equal to the first nights room and tax. For those receiving complimentary rooms, a cancellation fee will be charged.</p></body></html>",
      bookable: false,
      hasValidPrimary: false,
      discount: {
        percentage: "50%",
        value: "$449",
      },
      offerText: null,
      preAuthText: "One night room and tax deposit required.",
      perNightCompValue: "$450.00",
      perNightValue: "$899.00",
      lineItems: [
        [
          {
            title: "May 3 - 4",
            subtitle: "1 NIGHT",
            cost: {
              value: "$450.00",
              description: "AVG/NIGHT",
              style: "normal",
            },
            breakdown: {
              title: "PRICE BREAKDOWN",
              items: [
                {
                  title: "May 3",
                  value: "$899.00",
                  compValue: "$450.00",
                },
                {
                  title: "May 4",
                  value: "$899.00",
                  compValue: "$450.00",
                },
              ],
            },
          },
          {
            title: "Sub-Total",
            subtitle: null,
            cost: {
              value: "$450.00",
              description: null,
              style: "normal",
            },
            breakdown: null,
          },
          {
            title: "Taxes & Fees",
            subtitle: null,
            cost: {
              value: "$45.00",
              description: null,
              style: "normal",
            },
            breakdown: null,
          },
        ],
        [
          {
            title: "Total",
            subtitle: null,
            cost: {
              value: "$495.00",
              description: null,
              style: "normal",
            },
            breakdown: null,
          },
          {
            title: "DEPOSIT DUE NOW",
            subtitle: null,
            cost: {
              value: "$495.00",
              description: null,
              style: "highlighted",
            },
            breakdown: null,
          },
        ],
      ],
      total: "$495.00",
      estimatedTime: this.estimatedTime,
      guests: this.guests,
      payment: null,
      preferences: this.preferences,
      notes: this.note,
      canBookWithoutPayment: this.canBookWithoutPayment,
      paymentRequired: this.paymentRequired,
    };
  }

  get allPreferences(): IHotelPreference[] {
    return this.groupedPreferences.reduce(
      (allPreferences, current) => allPreferences.concat(current.preferences),
      [] as IHotelPreference[]
    );
  }

  get groupedPreferences(): IHotelPreferenceCategory[] {
    return [
      {
        category: "Location",
        preferences: [
          { code: "CRM", name: "Corner Room" },
          { code: "HLF", name: "High Level Floor" },
          { code: "LLF", name: "Low Level Floor" },
          { code: "MVR", name: "Mountain View" },
        ],
      },
      {
        category: "Room / Bed Type",
        preferences: [
          { code: "KIB", name: "King Bed" },
          { code: "QUB", name: "Queen Bed(s)" },
          { code: "UPG", name: "Upgrade" },
          { code: "SUT", name: "Suite" },
        ],
      },
      {
        category: "Features",
        preferences: [
          { code: "BNY", name: "Balcony" },
          { code: "DNT", name: "Dining Table" },
          { code: "LVR", name: "Living Room" },
          { code: "WLP", name: "Walking Patio" },
        ],
      },
      {
        category: "Bathroom",
        preferences: [
          { code: "RIS", name: "Roll-In Shower" },
          { code: "SHR", name: "Shower" },
          { code: "TIR", name: "Tub in Room" },
        ],
      },
      {
        category: "Other",
        preferences: [
          { code: "ECI", name: "Early Check-in" },
          { code: "LCI", name: "Late Check-in" },
          { code: "LAC", name: "Late Checkout" },
          { code: "HIR", name: "Hearing Impaired" },
        ],
      },
    ];
  }

  reset() {
    this.guests = [MockHotelBookingDataStore.mockPrimary];
    this.note = null;
    this.preferences = [];
  }
}

export const mockHotelStore = new MockHotelBookingDataStore();
